import { useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import produce from 'immer';
import dayjs from 'dayjs';

import { useCurrentAddressInfo } from '@/hooks';
import { request, formatNetworth, useCurrentUserId, formatNum } from '@/utils';

import { ArticleContributor, TopOfficialAccount, ContributionRecord } from '../types';
import { useUserXP } from '@/views/XP/hooks/xp';

const transContributor = (c: ArticleContributor) => {
  c._contribution = formatNetworth(c.contribution) || '$0';
  c._tvf = formatNetworth(c.tvf);
};

export const useArticleTopContributors = (id: number) => {
  const url = '/article/top_contributors';
  const { address: currentAddress } = useCurrentAddressInfo();
  const select = useCallback((res: { contributors: ArticleContributor[] }) => {
    if (!res?.contributors?.length) {
      return [];
    }

    return produce(res.contributors, draft => {
      draft
        .sort((m, n) => (n.contribution || 0) - (m.contribution || 0))
        .forEach(x => transContributor(x));
    });
  }, []);

  return useQuery(
    [url, currentAddress],
    () => {
      return request.get<{ contributors: ArticleContributor[] }>(url, {
        id,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!id,
      select,
    },
  );
};

const CacheTime = 10 * 60 * 1000;

export const use24hTopContributors = () => {
  const url = '/feed_trend/top_contributors';
  const { address: currentAddress } = useCurrentAddressInfo();

  const select = useCallback((res: { contributors: ArticleContributor[] }) => {
    if (!res?.contributors?.length) {
      return [];
    }

    return produce(res.contributors, draft => {
      draft
        .sort((m, n) => (n.contribution || 0) - (m.contribution || 0))
        .forEach(x => {
          x._contribution = formatNetworth(x.contribution) || '$0';
        });
    });
  }, []);

  return useQuery(
    [url, currentAddress],
    () => {
      return request.get<{ contributors: ArticleContributor[] }>(url, { limit: 5 });
    },
    {
      staleTime: CacheTime,
      cacheTime: CacheTime,
      select,
    },
  );
};

export const useArticleMyContribution = (id: number | false) => {
  const { isVerified, address: currentAddress } = useCurrentAddressInfo();
  const url = '/article/contributor';

  const select = useCallback((res: { contributor: ArticleContributor }) => {
    return produce(res.contributor, draft => {
      transContributor(draft);
    });
  }, []);

  return useQuery(
    [url, currentAddress],
    () => {
      return request.get<{ contributor: ArticleContributor }>(url, {
        id,
        user_id: currentAddress!,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: !!id && !!isVerified,
      select,
    },
  );
};

export const use24hMyContribution = (enabled?: boolean) => {
  const { isVerified, address: currentAddress } = useCurrentAddressInfo();
  const url = '/feed_trend/contributor';

  const select = useCallback((res: { contributor: ArticleContributor }) => {
    return produce(res.contributor, draft => {
      transContributor(draft);
    });
  }, []);

  return useQuery(
    [url, currentAddress],
    () => {
      return request.get<{ contributor: ArticleContributor }>(url, {
        user_id: currentAddress!,
      });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      enabled: enabled && !!isVerified,
      select,
    },
  );
};

export const useTopOfficials = () => {
  const url = '/feed_trend/top_officials';
  const { address: currentAddress } = useCurrentAddressInfo();

  const select = useCallback((res: { officials: TopOfficialAccount[] }) => {
    if (!res?.officials?.length) {
      return [];
    }
    return produce(res.officials, draft => {
      draft
        .sort((m, n) => (n.contribution || 0) - (m.contribution || 0))
        .forEach(x => {
          x._contribution = formatNetworth(x.contribution) || '$0';
        });
    });
  }, []);

  return useQuery(
    ['feed-sidebar-official', currentAddress, url],
    () => {
      return request.get<{
        officials: TopOfficialAccount[];
      }>(url, { limit: 5 });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      select,
    },
  );
};

// [timestamp, value]
type DisseminationValue = [number, number];

export const useArticleDisseminationTrend = (id: number) => {
  const url = '/article/dissemination_trend';

  const select = useCallback((res: { value_list: DisseminationValue[] }) => {
    if (!res?.value_list?.length) {
      return [];
    }

    return res.value_list.map<ContributionRecord>(x => ({
      value: x[1],
      timestamp: x[0],
      _date: dayjs.unix(x[0]).format('MMM DD'),
      _value: formatNetworth(x[1]),
    }));
  }, []);

  return useQuery(
    [url],
    () => {
      return request.get<{ value_list: DisseminationValue[] }>(url, { id });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      select,
    },
  );
};

export const useUserContributionTrend = (user_id: string) => {
  const url = '/feed_trend/contribution_trend';

  const select = useCallback((res: { value_list: DisseminationValue[] }) => {
    if (!res?.value_list?.length) {
      return [];
    }

    return res.value_list.map<ContributionRecord>(x => ({
      value: x[1],
      timestamp: x[0],
      _date: dayjs.unix(x[0]).format('MMM DD'),
      _value: formatNetworth(x[1]),
    }));
  }, []);

  return useQuery(
    [url],
    () => {
      return request.get<{ value_list: DisseminationValue[] }>(url, { user_id });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      select,
    },
  );
};

export const useNewlyOfficials = () => {
  const url = '/feed_trend/new_officials';
  const { address: currentAddress } = useCurrentAddressInfo();

  return useQuery(
    ['feed-sidebar-official', currentAddress, url],
    () => {
      return request.get<{
        officials: OfficialAccount[];
      }>(url, { limit: 5 });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      select: data => data.officials,
    },
  );
};

export type XP24hRankUser = {
  id: string;
  rank_24h: number;
  rank_24h_xp: number;
  level: number;
  is_following: boolean;
  is_followed: boolean;
} & ArticleContributor;

export const use24TopXPUser = () => {
  const url = '/xp/24h_rank_list';
  const userId = useCurrentUserId();

  const select = useCallback((res: { rank_list: XP24hRankUser[] }) => {
    if (!res?.rank_list?.length) {
      return [];
    }
    return produce(res.rank_list, draft => {
      draft.forEach(x => {
        x._contribution = formatNum(x.rank_24h_xp) || '-';
      });
    });
  }, []);

  const query = useQuery(
    [url, userId],
    () => {
      return request.get<{
        rank_list: XP24hRankUser[];
        total_count: number;
      }>(url, {
        start: 0,
        limit: 5,
      });
    },
    { select },
  );

  return query;
};

export const use24MyXp = () => {
  const { userInfo } = useCurrentAddressInfo();
  const { data: userXP, isLoading } = useUserXP();

  const data = useMemo(() => {
    if (!userXP || !userInfo) {
      return;
    }
    return {
      ...userInfo,
      _contribution: userXP?.rank_24h_xp ? formatNum(userXP?.rank_24h_xp) : '0',
    };
  }, [userInfo, userXP]) as ArticleContributor;

  return { data, isLoading };
};
